'use client'

import React, { useEffect } from 'react'
import { cn } from '~/lib/common-utils'
export type MessageType = 'success' | 'error'

export interface MessageProps {
  status?: MessageType
  message: string
  duration?: number
  onClose?: () => void
  className?: string
}

const icons = {
  success: (
    <svg width="13" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.00098 0.229492C10.4643 0.229492 13.2715 3.03671 13.2715 6.5C13.2715 9.96329 10.4643 12.7705 7.00098 12.7705C3.53769 12.7705 0.730469 9.96329 0.730469 6.5C0.730469 3.03671 3.53769 0.229492 7.00098 0.229492ZM9.72852 4.32031C9.49109 4.10268 9.12201 4.11817 8.9043 4.35547L6.10742 7.40625L5.08008 6.37891C4.85226 6.15127 4.48263 6.15116 4.25488 6.37891C4.02713 6.60666 4.02724 6.97628 4.25488 7.2041L5.71387 8.66211C5.82637 8.77461 5.9796 8.83646 6.13867 8.83301C6.29757 8.82955 6.44818 8.76159 6.55566 8.64453L9.76465 5.14453C9.98229 4.9071 9.96583 4.53803 9.72852 4.32031Z"
        stroke="#047819"
        fill="white"
      />
    </svg>
  ),
  error: (
    <svg width="14" height="14" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.04364 0.675367C6.66457 0.469255 7.33373 0.469255 7.95466 0.675367C8.57303 0.880626 9.05854 1.36744 9.5463 2.05142C10.0324 2.73313 10.5696 3.68365 11.262 4.90891L11.2892 4.95701L11.2892 4.95704L11.2892 4.95704C11.9818 6.18247 12.5189 7.13293 12.8537 7.90373C13.1901 8.67828 13.3576 9.34705 13.2239 9.98952C13.0893 10.6364 12.7575 11.2247 12.2741 11.6693C11.7919 12.1128 11.1354 12.2999 10.3087 12.3898C9.48676 12.4791 8.41151 12.4791 7.02759 12.4791H6.97072C5.58679 12.4791 4.51155 12.4791 3.68956 12.3898C2.86289 12.2999 2.20636 12.1128 1.72419 11.6693C1.24085 11.2247 0.908963 10.6364 0.774373 9.98952C0.640699 9.34705 0.808234 8.67828 1.14462 7.90373C1.47937 7.13293 2.01652 6.18245 2.70907 4.95701L2.73625 4.90891L2.73626 4.90889C3.42869 3.68365 3.96586 2.73313 4.45201 2.05142C4.93977 1.36744 5.42528 0.880626 6.04364 0.675367ZM6.41582 9.41662C6.41582 9.09445 6.67582 8.83328 6.99654 8.83328H7.00176C7.32248 8.83328 7.58248 9.09445 7.58248 9.41662C7.58248 9.73878 7.32248 9.99995 7.00176 9.99995H6.99654C6.67582 9.99995 6.41582 9.73878 6.41582 9.41662ZM6.41582 7.08328C6.41582 7.40545 6.67698 7.66662 6.99915 7.66662C7.32132 7.66662 7.58248 7.40545 7.58248 7.08328V4.74995C7.58248 4.42778 7.32132 4.16662 6.99915 4.16662C6.67698 4.16662 6.41582 4.42778 6.41582 4.74995V7.08328Z"
        stroke="#E02424"
        fill="white"
      />
    </svg>
  ),
}

const statusStyles = {
  success: 'bg-[#047819] dark:bg-gray-800 dark:text-green-400',
  error: 'bg-[#E02424] dark:bg-gray-800 dark:text-red-400',
}

export default function Message({
  status = 'error',
  message,
  duration = 3000,
  onClose,
  className,
}: MessageProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose?.()
    }, duration)
    return () => clearTimeout(timer)
  }, [duration, onClose])

  return (
    <div
      className={cn(
        'fixed left-1/2 top-4 z-80 flex -translate-x-1/2 transform items-center space-x-2 rounded-lg p-2 font-medium text-white shadow-md transition-opacity duration-300',
        statusStyles[status],
        className
      )}
      role="alert"
    >
      <span className="flex items-center justify-center rounded-full">{icons[status]}</span>
      <span className="text-[12px]">{message}</span>
      {onClose && (
        <button onClick={onClose} className="p-1">
          <svg
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  )
}
